import { InfoOutlineIcon, WarningTwoIcon } from '@chakra-ui/icons'
import {
  Badge,
  Alert,
  AlertDescription,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { RefObject } from 'react'
import {
  EhrMedicationRequestStatus,
  EhrRecentMedicationRequestsResult,
} from './ehrLaunchPatientApi'
import { EhrRecentMedicationRequestsState } from './ehrRecentMedicationRequestsSlice'

interface RecentMedicationRequestsModalProps {
  result: EhrRecentMedicationRequestsResult
  onDismiss: () => void
  finalFocusRef: RefObject<HTMLElement | null>
  onReprescribe: (rowIndex: number, forceTextSearch: boolean) => void
  represcribing?: EhrRecentMedicationRequestsState['represcribing']
}

const ineligibleReason = {
  entered_in_error: 'Cannot re-prescribe: entered in error',
  not_an_order: 'Cannot re-prescribe: not an order',
  do_not_perform: 'Cannot re-prescribe: marked do not perform',
  medicine_unavailable: 'Cannot re-prescribe: medicine unavailable',
} as const

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const statusPresentation: Record<
  EhrMedicationRequestStatus,
  { label: string; colorScheme: string }
> = {
  active: { label: 'Active', colorScheme: 'green' },
  'on-hold': { label: 'On hold', colorScheme: 'orange' },
  completed: { label: 'Completed', colorScheme: 'blue' },
  cancelled: { label: 'Cancelled', colorScheme: 'red' },
  stopped: { label: 'Stopped', colorScheme: 'red' },
  'entered-in-error': { label: 'Entered in error', colorScheme: 'red' },
  draft: { label: 'Draft', colorScheme: 'gray' },
  unknown: { label: 'Unknown', colorScheme: 'gray' },
}

const formatAuthoredOn = (authoredOn: string | null) => {
  if (!authoredOn) return 'Date unavailable'
  if (/^\d{4}$/.test(authoredOn)) return authoredOn
  if (/^\d{4}-\d{2}$/.test(authoredOn)) {
    return `${MONTHS[Number(authoredOn.slice(5, 7)) - 1]} ${authoredOn.slice(0, 4)}`
  }
  const date = /^(\d{4})-(\d{2})-(\d{2})/.exec(authoredOn)
  if (!date) return 'Date unavailable'
  return `${Number(date[3])} ${MONTHS[Number(date[2]) - 1]} ${date[1]}`
}

const TerminalState = ({ unavailable }: { unavailable: boolean }) => (
  <VStack py={8} spacing={3} textAlign="center">
    {unavailable ? (
      <WarningTwoIcon aria-hidden color="orange.400" boxSize={8} />
    ) : (
      <InfoOutlineIcon aria-hidden color="blue.400" boxSize={8} />
    )}
    <Heading size="md">
      {unavailable ? 'Recent medications unavailable' : 'No medication requests found'}
    </Heading>
    <Text color="gray.600">
      {unavailable
        ? 'RxPad could not retrieve recent medications from the EHR. You can continue prescribing.'
        : 'The EHR returned no medication requests for this patient.'}
    </Text>
  </VStack>
)

export const RecentMedicationRequestsModal = ({
  result,
  onDismiss,
  finalFocusRef,
  onReprescribe,
  represcribing,
}: RecentMedicationRequestsModalProps) => {
  const medications = result.status === 'available' ? result.medications.slice(0, 3) : []
  const populated = result.status === 'available' && medications.length > 0

  return (
    <Modal
      isOpen
      onClose={onDismiss}
      finalFocusRef={finalFocusRef}
      isCentered
      size="lg"
      scrollBehavior="inside"
    >
      <ModalOverlay data-testid="recent-medications-overlay" />
      <ModalContent
        maxH="calc(100vh - 32px)"
        maxW="32rem"
        mx="16px"
        overflow="hidden"
        w="calc(100% - 32px)"
      >
        <ModalHeader>Latest medication requests</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          {populated ? (
            <>
              <Text color="gray.600" mb={6}>
                Sourced from the patient's EHR record. Newest requests appear first.
              </Text>
              <VStack align="stretch" spacing={0}>
                {medications.map((medication, index) => {
                  const status = statusPresentation[medication.status]
                  const eligible = medication.represcribing.eligibility === 'eligible'
                  const selected = represcribing?.rowIndex === index
                  const matching = selected && represcribing?.phase === 'matching'
                  const disabledByOtherAttempt = represcribing?.phase === 'matching' && !selected
                  return (
                    <Flex
                      data-testid="medication-request-row"
                      key={`${medication.authoredOn ?? 'undated'}-${index}`}
                      minW={0}
                    >
                      <Text
                        color="gray.600"
                        flex="0 0 92px"
                        fontSize="sm"
                        pr={4}
                        py={3}
                        textAlign="right"
                      >
                        {formatAuthoredOn(medication.authoredOn)}
                      </Text>
                      <Box
                        borderLeft={index === medications.length - 1 ? undefined : '1px solid'}
                        borderColor="gray.200"
                        minW={0}
                        pb={index === medications.length - 1 ? 0 : 4}
                        pl={5}
                        position="relative"
                        pt={3}
                      >
                        <Box
                          aria-hidden
                          bg="blue.400"
                          borderRadius="full"
                          boxSize="8px"
                          left="-4.5px"
                          position="absolute"
                          top="17px"
                        />
                        <Text fontWeight="bold" overflowWrap="anywhere">
                          {medication.name}
                        </Text>
                        <Badge
                          colorScheme={status.colorScheme}
                          data-color-scheme={status.colorScheme}
                          mt={1}
                        >
                          {status.label}
                        </Badge>
                        {eligible ? (
                          <Button
                            aria-label={
                              matching
                                ? `Finding ${medication.name}`
                                : `Re-prescribe ${medication.name}`
                            }
                            colorScheme="blue"
                            isDisabled={matching || disabledByOtherAttempt}
                            mt={3}
                            onClick={() => onReprescribe(index, false)}
                            size="sm"
                            w={{ base: '100%', md: 'auto' }}
                          >
                            {matching ? 'Finding medicine…' : 'Re-prescribe'}
                          </Button>
                        ) : (
                          <Text color="gray.600" fontSize="sm" mt={2}>
                            {
                              ineligibleReason[
                                medication.represcribing
                                  .eligibility as keyof typeof ineligibleReason
                              ]
                            }
                          </Text>
                        )}
                        {selected && represcribing?.phase === 'error' && (
                          <Alert
                            alignItems="flex-start"
                            flexDirection="column"
                            mt={3}
                            status="error"
                          >
                            <AlertDescription>{represcribing.message}</AlertDescription>
                            <Button
                              mt={2}
                              onClick={() => onReprescribe(index, true)}
                              size="sm"
                              variant="outline"
                            >
                              Search by EHR name
                            </Button>
                          </Alert>
                        )}
                      </Box>
                    </Flex>
                  )
                })}
              </VStack>
            </>
          ) : (
            <TerminalState unavailable={result.status === 'unavailable'} />
          )}
        </ModalBody>
        <ModalFooter flexShrink={0}>
          <Button colorScheme="blue" onClick={onDismiss}>
            Continue prescribing
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
